import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, FileUp } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { api, errMsg } from "../lib/api";
import { useApi, StatusChip, Field, btn, inputCls } from "./kit";

export const LEVEL = { 1: "Below expectations", 2: "Meets expectations", 3: "Exceeds expectations" };

const ST = {
  self_review: ["not_started", "Self-review"],
  manager_review: ["in_progress", "Manager review"],
  awaiting_signatures: ["awaiting_signoff", "Waiting for signatures"],
  completed: ["completed", "Complete"],
};

export function ReviewStatus({ r }) {
  const [s, l] = ST[r.status] || ["draft", r.status];
  return <StatusChip status={s} label={l} />;
}

export function PastReview({ uid, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", review_date: "", file: null });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f.file); fd.append("review_date", f.review_date); fd.append("title", f.title);
      await api.post(`/people/${uid}/past-reviews`, fd);
      toast.success("Past review added to their file."); setOpen(false); setF({ title: "", review_date: "", file: null }); onDone?.();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className={btn.ghost} data-testid="past-review-open"><FileUp className="h-4 w-4" /> Add a past review</button></DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Add a past performance review</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">For reviews done and signed on paper. It's filed under their Performance reviews and documents, with no signing needed.</p>
        <form onSubmit={submit} className="space-y-4" data-testid="past-review-form">
          <Field label="Title" htmlFor="pr-t"><input id="pr-t" className={inputCls} placeholder="e.g. 2024 annual review" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="past-review-title" /></Field>
          <Field label="Review date" htmlFor="pr-d"><input id="pr-d" type="date" required max={new Date().toLocaleDateString("en-CA", { timeZone: "America/Toronto" })} className={inputCls} value={f.review_date} onChange={(e) => setF({ ...f, review_date: e.target.value })} data-testid="past-review-date" /></Field>
          <Field label="File (PDF, Word or photo)" htmlFor="pr-f"><input id="pr-f" type="file" required accept=".pdf,.docx,.jpg,.jpeg,.png,.heic,image/*" className={inputCls} onChange={(e) => setF({ ...f, file: e.target.files?.[0] || null })} data-testid="past-review-file" /></Field>
          <button className={`${btn.primary} w-full`} disabled={busy || !f.file || !f.review_date} data-testid="past-review-submit">{busy ? "Uploading…" : "Add past review"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function StartReview({ uid }) {
  const nav = useNavigate();
  const tpls = useApi("/review-templates");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ template_id: "", period: "", self_due: "", skip_self: false });
  const submit = async (e) => {
    e.preventDefault();
    try { const r = await api.post("/reviews", { ...f, employee_id: uid, self_due: f.self_due || null }); toast.success(f.skip_self ? "Review started." : "Review started. They've been emailed to do their self-review."); nav(`/performance/${r.data.id}`); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className={btn.ghost} data-testid="start-review-open"><ClipboardList className="h-4 w-4" /> Start performance review</button></DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Start a performance review</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="start-review-form">
          <Field label="Template" htmlFor="sr-t" hint={(tpls.data || []).length ? null : "No templates yet. Build one under Performance reviews → Templates."}>
            <select id="sr-t" required className={inputCls} value={f.template_id} onChange={(e) => setF({ ...f, template_id: e.target.value })} data-testid="start-review-template">
              <option value="">Choose…</option>{(tpls.data || []).filter((t) => (!t.import_status || t.import_status === "ready") && t.competencies.length).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></Field>
          <Field label="Review period" htmlFor="sr-p"><input id="sr-p" className={inputCls} placeholder="e.g. 2026 annual" value={f.period} onChange={(e) => setF({ ...f, period: e.target.value })} data-testid="start-review-period" /></Field>
          {!f.skip_self && <Field label="Self-review due" htmlFor="sr-d"><input id="sr-d" type="date" className={inputCls} value={f.self_due} onChange={(e) => setF({ ...f, self_due: e.target.value })} data-testid="start-review-due" /></Field>}
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-5 w-5" checked={f.skip_self} onChange={(e) => setF({ ...f, skip_self: e.target.checked })} data-testid="start-review-skip-self" /> Skip the self-review</label>
          <button className={`${btn.primary} w-full`} data-testid="start-review-submit">Start review</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
